// ═══════════════════════════════════════════════════════════════
// DataMemoryMap — ITRIIA Data & Memory Map (Lot C, v1 SOBRE)
// Carte interne (admin) : performances, flux documentaires et traces de
// contexte IA — provenance PRUDENTE (jamais « preuve » / « source garantie »).
// Réutilise l'existant (Infrastructure, CostDashboard) sans le dupliquer.
// Priorité v1 : lisibilité / vérité / debug, pas le « waouh » visuel.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { Network, Gauge, FileStack, GitBranch, CheckSquare, RefreshCw, Info, ClipboardList, Download, AlertTriangle, ShieldCheck } from 'lucide-react';
import InfrastructureDashboard from '../InfrastructureDashboard';
import CostDashboard from '../../analytics/CostDashboard';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('oracle_token')}` });
const fmtMs = (v) => (v == null ? '—' : `${v} ms`);

const TABS = [
  { key: 'services', label: 'Services', icon: <Network size={15} /> },
  { key: 'perf', label: 'Performance / coût', icon: <Gauge size={15} /> },
  { key: 'flux', label: 'Flux documentaires', icon: <FileStack size={15} /> },
  { key: 'provenance', label: 'Provenance', icon: <GitBranch size={15} /> },
  { key: 'anomalies', label: 'Anomalies', icon: <AlertTriangle size={15} /> },
  { key: 'decision', label: 'Décision', icon: <CheckSquare size={15} /> },
];

const card = {
  background: 'var(--card-bg)', border: '1px solid var(--card-border)',
  borderRadius: '12px', padding: '20px',
};
const noticeBox = {
  ...card, borderLeft: '3px solid #8b5cf6', display: 'flex', gap: '10px',
  alignItems: 'flex-start', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5,
};

// ─────────────────────────────────────────────────────────────
// Onglet Performance — latence par phase + p95 (GET /api/costs/perf)
// ─────────────────────────────────────────────────────────────
function PerfPanel({ restricted = false }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/costs/perf?days=${days}`, { headers: authHeaders() });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error('perf', e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const phases = data?.by_phase || {};
  const phaseRows = [
    ['Mémoire', phases.memory_ms],
    ["Bases d'expertise", phases.expertise_bases_ms],
    ['Outils', phases.tools_ms],
    ['Génération LLM', phases.llm_ms],
    ['Sauvegarde', phases.saving_ms],
    ['Non instrumenté', phases.unattributed_ms],
  ];
  const maxPhase = Math.max(1, ...phaseRows.map(([, v]) => v || 0));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '6px' }}>
          {[7, 14, 30].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{
              padding: '6px 12px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer',
              border: '1px solid var(--card-border)',
              background: days === d ? 'linear-gradient(135deg, #7c3aed, #6d28d9)' : 'var(--input-bg)',
              color: days === d ? '#fff' : 'var(--text-secondary)',
            }}>{d}j</button>
          ))}
        </div>
        <button onClick={load} style={{
          padding: '6px 12px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer',
          border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text-secondary)',
          display: 'flex', alignItems: 'center', gap: '6px',
        }}><RefreshCw size={13} /> Rafraîchir</button>
      </div>

      {loading && <div style={{ ...card, textAlign: 'center', color: 'var(--text-secondary)' }}>Chargement…</div>}

      {!loading && data && (
        <>
          {/* Latence moyenne par phase d'un tour de chat projet */}
          <div style={card}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', marginBottom: '4px' }}>
              Latence moyenne par phase (chat projet)
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '14px' }}>
              {phases.samples || 0} tour(s) instrumenté(s) · total requête moyen {fmtMs(phases.total_request_ms)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {phaseRows.map(([label, val]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '140px', fontSize: '12px', color: 'var(--text-secondary)' }}>{label}</div>
                  <div style={{ flex: 1, height: '14px', background: 'var(--input-bg)', borderRadius: '7px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${((val || 0) / maxPhase) * 100}%`, height: '100%',
                      background: label === 'Non instrumenté' ? '#9ca3af' : 'linear-gradient(90deg, #8b5cf6, #6d28d9)',
                    }} />
                  </div>
                  <div style={{ width: '70px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: 'var(--text)' }}>{fmtMs(val)}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '10px', fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              « Non instrumenté » = temps réel non mesuré (session, prompt, nettoyage). On ne l'invente pas.
            </div>
          </div>

          {/* p95 par modèle */}
          <div style={card}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', marginBottom: '12px' }}>
              Latence LLM par modèle (avg · p95)
            </div>
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: 'var(--text-secondary)', textAlign: 'left' }}>
                  <th style={{ padding: '4px 0' }}>Modèle</th>
                  <th style={{ textAlign: 'right' }}>Échantillons</th>
                  <th style={{ textAlign: 'right' }}>Moyenne</th>
                  <th style={{ textAlign: 'right' }}>p95</th>
                </tr>
              </thead>
              <tbody>
                {(data.by_model || []).map((m) => (
                  <tr key={m.model} style={{ borderTop: '1px solid var(--card-border)', color: 'var(--text)' }}>
                    <td style={{ padding: '6px 0', wordBreak: 'break-all' }}>{m.model}</td>
                    <td style={{ textAlign: 'right' }}>{m.samples}</td>
                    <td style={{ textAlign: 'right' }}>{fmtMs(m.avg_latency_ms)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMs(m.p95_latency_ms)}</td>
                  </tr>
                ))}
                {(data.by_model || []).length === 0 && (
                  <tr><td colSpan={4} style={{ padding: '12px 0', color: 'var(--text-secondary)' }}>Aucune mesure sur la période.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Coûts détaillés (cross-projet) — masqués en mode restreint (fondateur) */}
          {!restricted && <CostDashboard />}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Onglet Provenance — traces de contexte (GET /api/map/...)
// Libellés PRUDENTS : « contexte consulté / injecté », jamais « preuve ».
// ─────────────────────────────────────────────────────────────
const RELATION_LABEL = {
  consulted: 'Consulté seul', injected: 'Injecté dans le contexte',
  tool_result: "Résultat d'outil", explicit_citation: 'Cité explicitement', validated: 'Validé',
};
const RELATION_COLOR = {
  consulted: '#9ca3af', injected: '#8b5cf6', tool_result: '#3b82f6',
  explicit_citation: '#10b981', validated: '#b45309',
};
const CONFIDENCE_LABEL = { low: 'basse', medium: 'moyenne', high: 'haute' };
const CONFIDENCE_COLOR = { low: '#9ca3af', medium: '#f59e0b', high: '#10b981' };
const PHASE_LABEL = {
  memory: 'Mémoire', expertise_bases: "Bases d'expertise", tools: 'Outils',
  llm: 'LLM', saving: 'Sauvegarde', unattributed: 'Non instrumenté',
};
const fmtDur = (ms) => (ms == null ? null : ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`);

function RelationBadge({ type }) {
  const color = RELATION_COLOR[type] || '#9ca3af';
  return (
    <span style={{
      padding: '2px 8px', borderRadius: '10px', fontSize: '10.5px', fontWeight: 600,
      background: `${color}22`, color, whiteSpace: 'nowrap',
    }}>{RELATION_LABEL[type] || type}</span>
  );
}

function ChunkRow({ c }) {
  const [open, setOpen] = useState(false);
  const ref = c.document_id != null
    ? `doc #${c.document_id}${c.chunk_index != null ? ` · chunk ${c.chunk_index}` : ''}`
    : `fichier #${c.file_id}${c.chunk_index != null ? ` · chunk ${c.chunk_index}` : ''}`;
  const snippet = c.metadata?.snippetPreview;
  return (
    <div style={{ padding: '8px 0', borderTop: '1px solid var(--card-border)', fontSize: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ color: 'var(--text)', fontWeight: 500, minWidth: '170px' }}>{ref}</span>
        {c.retrieval_score != null && (
          <span style={{ color: 'var(--text-secondary)' }}
            title="Score de récupération : proximité du résultat lors de la recherche, pas une certitude de source.">
            score de récupération&nbsp;: {Math.round(c.retrieval_score * 100)}%
          </span>
        )}
        {c.metadata?.filename && <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '260px' }}>{c.metadata.filename}</span>}
        {snippet && (
          <button onClick={() => setOpen(o => !o)} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#8b5cf6',
            fontSize: '11px', padding: 0, textDecoration: 'underline',
          }}>{open ? 'Masquer' : 'Voir extrait'}</button>
        )}
        <span style={{ marginLeft: 'auto' }}><RelationBadge type={c.relation_type} /></span>
      </div>
      {open && snippet && (
        <div style={{
          marginTop: '6px', padding: '8px 10px', borderRadius: '6px', background: 'var(--input-bg)',
          color: 'var(--text-secondary)', fontSize: '11.5px', lineHeight: 1.45, whiteSpace: 'pre-wrap',
          fontFamily: 'ui-monospace, monospace',
        }}>{snippet}{snippet.length >= 300 ? '…' : ''}</div>
      )}
    </div>
  );
}

function CopyTrace({ traceId }) {
  const [copied, setCopied] = useState(false);
  if (!traceId) return null;
  const short = `${traceId.slice(0, 8)}…${traceId.slice(-4)}`;
  return (
    <button
      onClick={() => { navigator.clipboard?.writeText(traceId); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
      title={traceId}
      style={{
        background: 'var(--input-bg)', border: '1px solid var(--card-border)', borderRadius: '6px',
        padding: '2px 8px', fontSize: '11px', cursor: 'pointer', color: 'var(--text-secondary)',
        fontFamily: 'ui-monospace, monospace',
      }}>
      trace: {copied ? 'copié ✓' : short}
    </button>
  );
}

function LatencyStrip({ latency }) {
  if (!latency) return null;
  const order = ['memory', 'expertise_bases', 'tools', 'llm', 'saving', 'unattributed'];
  const parts = order.filter(k => latency[k] != null && latency[k] > 0)
    .map(k => `${PHASE_LABEL[k]} ${fmtDur(latency[k])}`);
  if (parts.length === 0) return null;
  return (
    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '10px' }}>
      <strong>Latence du tour</strong>{latency.total_request_ms != null ? ` · total ${fmtDur(latency.total_request_ms)}` : ''} — {parts.join(' · ')}
    </div>
  );
}

function ProvenancePanel() {
  const [traces, setTraces] = useState([]);
  const [selected, setSelected] = useState(null);
  const [env, setEnv] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phase 0a — filtre par type de relation
  const [filter, setFilter] = useState({ injected: true, tool_result: true, consulted: true });
  // Phase 1 — fiche d'audit du tour
  const [audit, setAudit] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [answerOpen, setAnswerOpen] = useState(false);

  const loadTraces = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/map/traces/recent?limit=30', { headers: authHeaders() });
      if (res.ok) { const j = await res.json(); setTraces(j.traces || []); }
    } catch (e) { console.error('traces', e); }
    finally { setLoading(false); }
  };
  const loadProvenance = async (traceId) => {
    setSelected(traceId); setEnv(null); setAudit(null); setAuditOpen(false); setAnswerOpen(false);
    try {
      const res = await fetch(`/api/map/traces/${traceId}/provenance`, { headers: authHeaders() });
      if (res.ok) setEnv(await res.json());
    } catch (e) { console.error('provenance', e); }
  };
  const toggleAudit = async () => {
    const next = !auditOpen; setAuditOpen(next);
    if (next && !audit && selected) {
      try {
        const res = await fetch(`/api/map/traces/${selected}/audit`, { headers: authHeaders() });
        if (res.ok) setAudit(await res.json());
      } catch (e) { console.error('audit', e); }
    }
  };
  const exportAudit = () => {
    if (!audit) return;
    const blob = new Blob([JSON.stringify(audit, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `audit-${(audit.trace_id || 'tour').slice(0, 8)}.json`;
    a.click(); URL.revokeObjectURL(url);
  };
  const toggleFilter = (k) => setFilter(f => ({ ...f, [k]: !f[k] }));
  // Phase 3 — validation humaine (pont Narrative Ownership), réversible.
  const [validating, setValidating] = useState(false);
  const toggleValidation = async () => {
    if (!selected || validating) return;
    setValidating(true);
    const action = env?.validated ? 'unvalidate' : 'validate';
    try {
      const res = await fetch(`/api/map/traces/${selected}/${action}`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: '{}',
      });
      if (res.ok) await loadProvenance(selected);
    } catch (e) { console.error('validate', e); }
    finally { setValidating(false); }
  };
  useEffect(() => { loadTraces(); }, []);

  const FILTER_DEFS = [
    { key: 'injected', label: 'Injectés' },
    { key: 'tool_result', label: "Résultats d'outil" },
    { key: 'consulted', label: 'Consultés' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={noticeBox}>
        <Info size={16} style={{ color: '#8b5cf6', flexShrink: 0, marginTop: '1px' }} />
        <span>
          Ce sont des <strong>traces de contexte</strong> : éléments récupérés et/ou injectés pendant la
          génération. <strong>Pas une preuve causale</strong> que la réponse en dérive. La provenance déclarée
          reflète le type de relation, pas une certitude.
        </span>
      </div>

      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        {/* Liste des tours tracés */}
        <div style={{ ...card, flex: '0 0 320px', maxHeight: '460px', overflow: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>Tours tracés</span>
            <button onClick={loadTraces} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>
              <RefreshCw size={13} />
            </button>
          </div>
          {loading && <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Chargement…</div>}
          {!loading && traces.length === 0 && (
            <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
              Aucune trace encore. Pose une question dans un projet (avec recherche de fichiers/bases) pour en générer.
            </div>
          )}
          {traces.map(t => (
            <button key={t.trace_id} onClick={() => loadProvenance(t.trace_id)} style={{
              display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', marginBottom: '6px',
              borderRadius: '8px', cursor: 'pointer', fontSize: '12px',
              border: '1px solid var(--card-border)',
              background: selected === t.trace_id ? 'rgba(139,92,246,0.12)' : 'var(--input-bg)',
              color: 'var(--text)',
            }}>
              <div style={{ fontWeight: 600 }}>{t.project_name || 'Projet ?'}</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                {t.refs} réf{t.total_request_ms != null ? ` · ${fmtDur(t.total_request_ms)}` : ''} · {new Date(t.created_at).toLocaleString('fr-FR')}
              </div>
            </button>
          ))}
        </div>

        {/* Détail provenance */}
        <div style={{ ...card, flex: 1, minWidth: '320px' }}>
          {!selected && <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Sélectionne un tour à gauche.</div>}
          {selected && !env && <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Chargement…</div>}
          {env && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>Provenance déclarée :</span>
                <span style={{
                  padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
                  background: `${CONFIDENCE_COLOR[env.confidence]}22`, color: CONFIDENCE_COLOR[env.confidence],
                }}>{CONFIDENCE_LABEL[env.confidence] || env.confidence}</span>
                <CopyTrace traceId={env.trace_id} />
                <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  Contexte&nbsp;: {env.counts.injected} injecté(s) · Outils&nbsp;: {env.counts.tool_result} résultat(s) · Consultés seuls&nbsp;: {env.counts.consulted}
                </span>
              </div>

              <LatencyStrip latency={env.latency_by_phase} />

              {/* Phase 3 — validation humaine + citations explicites déclarées */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                {env.validated ? (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                    padding: '3px 10px', borderRadius: '12px', fontSize: '11.5px', fontWeight: 600,
                    background: 'rgba(16,185,129,0.12)', color: '#10b981',
                  }} title={env.validation?.note || ''}>
                    <ShieldCheck size={13} /> Validé par {env.validation?.by || '—'}
                    {env.validation?.at ? ` · ${new Date(env.validation.at).toLocaleDateString('fr-FR')}` : ''}
                  </span>
                ) : null}
                <button onClick={toggleValidation} disabled={validating} style={{
                  padding: '4px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                  cursor: validating ? 'wait' : 'pointer', border: '1px solid var(--card-border)',
                  background: env.validated ? 'var(--input-bg)' : 'rgba(16,185,129,0.12)',
                  color: env.validated ? 'var(--text-secondary)' : '#10b981',
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                }}>
                  <ShieldCheck size={13} /> {env.validated ? 'Annuler la validation' : 'Valider cette provenance'}
                </button>
                {env.citations && env.citations.declared > 0 && (
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
                    Citations déclarées&nbsp;: <strong>{env.citations.declared}</strong>
                    {' · '}<span style={{ color: '#10b981' }}>{env.citations.supported} étayée(s)</span>
                    {env.citations.unsupported > 0 && <>{' · '}<span style={{ color: '#ef4444' }}>{env.citations.unsupported} non étayée(s)</span></>}
                  </span>
                )}
              </div>

              {/* Liste des citations non étayées (signal honnête, jamais une preuve) */}
              {env.citations && env.citations.unsupported > 0 && (
                <div style={{ ...noticeBox, borderLeftColor: '#ef4444', marginBottom: '12px' }}>
                  <Info size={16} style={{ color: '#ef4444', flexShrink: 0, marginTop: '1px' }} />
                  <span>
                    <strong>{env.citations.unsupported} citation(s) non étayée(s)</strong> : le modèle a cité un extrait
                    qui ne lui a pas été injecté ({env.citations.items.filter(c => c.metadata?.supported === false).map(c => `#${c.file_id}:${c.chunk_index}`).join(', ')}).
                    Signal de fiabilité — pas une preuve d'erreur de la réponse.
                  </span>
                </div>
              )}

              {/* Contrôles : filtre relation_type + fiche d'audit */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Afficher :</span>
                {FILTER_DEFS.map(f => (
                  <button key={f.key} onClick={() => toggleFilter(f.key)} style={{
                    padding: '2px 9px', borderRadius: '10px', fontSize: '10.5px', fontWeight: 600, cursor: 'pointer',
                    border: '1px solid ' + (filter[f.key] ? RELATION_COLOR[f.key] : 'var(--card-border)'),
                    background: filter[f.key] ? `${RELATION_COLOR[f.key]}22` : 'transparent',
                    color: filter[f.key] ? RELATION_COLOR[f.key] : 'var(--text-secondary)',
                  }}>{f.label}</button>
                ))}
                <button onClick={toggleAudit} style={{
                  marginLeft: 'auto', padding: '4px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                  cursor: 'pointer', border: '1px solid var(--card-border)',
                  background: auditOpen ? 'rgba(139,92,246,0.12)' : 'var(--input-bg)', color: 'var(--text)',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}><ClipboardList size={13} /> Fiche d'audit</button>
              </div>

              {/* Fiche d'audit du tour (Phase 1) — question/réponse/coût + export */}
              {auditOpen && (
                <div style={{ ...card, marginBottom: '14px', borderLeft: '3px solid #8b5cf6' }}>
                  {!audit && <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Chargement de la fiche…</div>}
                  {audit && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)' }}>Fiche d'audit du tour</span>
                        {audit.model && <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{audit.model}</span>}
                        {audit.cost && (
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            ${audit.cost.cost_usd != null ? audit.cost.cost_usd.toFixed(4) : '—'} · {audit.cost.input_tokens ?? '—'} in / {audit.cost.output_tokens ?? '—'} out
                          </span>
                        )}
                        <button onClick={exportAudit} style={{
                          marginLeft: 'auto', padding: '3px 10px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer',
                          border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text-secondary)',
                          display: 'flex', alignItems: 'center', gap: '5px',
                        }}><Download size={12} /> Exporter JSON</button>
                      </div>
                      {audit.question && (
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)' }}>Question</div>
                          <div style={{ fontSize: '12.5px', color: 'var(--text)' }}>{audit.question}</div>
                        </div>
                      )}
                      {audit.answer && (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)' }}>Réponse</span>
                            <button onClick={() => setAnswerOpen(o => !o)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8b5cf6', fontSize: '11px', padding: 0, textDecoration: 'underline' }}>
                              {answerOpen ? 'Réduire' : 'Voir'}
                            </button>
                          </div>
                          <div style={{
                            fontSize: '12.5px', color: 'var(--text)', whiteSpace: 'pre-wrap', lineHeight: 1.45,
                            maxHeight: answerOpen ? 'none' : '64px', overflow: 'hidden',
                            position: 'relative',
                          }}>{audit.answer}</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {filter.injected && env.chunks_injected.length > 0 && (
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Éléments injectés dans le contexte</div>
                  {env.chunks_injected.map((c, i) => <ChunkRow key={`inj-${i}`} c={c} />)}
                </div>
              )}

              {filter.tool_result && env.tool_calls.length > 0 && (
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Résultats d'outils</div>
                  {env.tool_calls.map((tc, i) => (
                    <div key={`tc-${i}`} style={{ marginBottom: '8px' }}>
                      <div style={{ fontSize: '12px', color: 'var(--text)' }}>
                        <strong>{tc.tool_name}</strong>{tc.tool_query ? ` · « ${tc.tool_query} »` : ''}
                      </div>
                      {tc.chunks.map((c, j) => <ChunkRow key={`tc-${i}-${j}`} c={c} />)}
                    </div>
                  ))}
                </div>
              )}

              {filter.consulted && (env.chunks_consulted || []).length > 0 && (
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Consultés seuls</div>
                  {env.chunks_consulted.map((c, i) => <ChunkRow key={`con-${i}`} c={c} />)}
                </div>
              )}

              {env.chunks_injected.length === 0 && env.tool_calls.length === 0 && (
                <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.5 }}>
                  Aucune trace de contexte captée pour ce tour. <strong>Cela ne signifie pas que la réponse est fausse</strong> —
                  seulement qu'aucun chunk ni document n'a été récupéré ou injecté pendant cette génération
                  (réponse sur le modèle seul). Provenance déclarée «&nbsp;basse&nbsp;».
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Onglets sobres restants
// ─────────────────────────────────────────────────────────────
function FluxPanel() {
  return (
    <div style={noticeBox}>
      <Info size={16} style={{ color: '#8b5cf6', flexShrink: 0, marginTop: '1px' }} />
      <span>
        <strong>Flux documentaires</strong> — v1. Le flux <code>fichier → chunks → embeddings → Weaviate</code>
        est déjà mesuré par projet dans <em>Analytics → Statistiques projet</em> (composant ProjectAnalytics).
        L'agrégation transverse façon carte arrive après validation des traces de provenance. On n'affiche pas
        de chiffres qu'on ne mesure pas encore.
      </span>
    </div>
  );
}
function DecisionPanel() {
  return (
    <div style={noticeBox}>
      <Info size={16} style={{ color: '#8b5cf6', flexShrink: 0, marginTop: '1px' }} />
      <span>
        <strong>Décision</strong> — placeholder assumé (v1). Cette sous-carte reliera plus tard les actes de
        <em> Narrative Ownership</em> (consolider / suspendre) à la trace : qui a validé quoi, quelle synthèse,
        quel projet impacté. Pas encore branchée pour ne rien afficher de non vérifié.
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Onglet Anomalies (v1.3) — signaux d'exploitation honnêtes (GET /api/map/anomalies)
// JAMAIS un score de qualité : latence/p95, déséquilibres de provenance.
// ─────────────────────────────────────────────────────────────
const SEVERITY = {
  warning: { color: '#f59e0b', label: 'À surveiller' },
  info: { color: '#06b6d4', label: 'Information' },
};

function AnomaliesPanel() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/map/anomalies?days=${days}`, { headers: authHeaders() });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error('anomalies', e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const anomalies = data?.anomalies || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={noticeBox}>
        <Info size={16} style={{ color: '#8b5cf6', flexShrink: 0, marginTop: '1px' }} />
        <span>
          Ce sont des <strong>signaux d'exploitation</strong> (latence, déséquilibres de récupération de contexte) —
          <strong> pas un jugement de qualité</strong> ni une preuve d'erreur. Aucun score synthétique : on n'affiche
          qu'un écart mesuré à une cible, jamais une « vérité » sur la réponse.
        </span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '6px' }}>
          {[7, 14, 30].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{
              padding: '6px 12px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer',
              border: '1px solid var(--card-border)',
              background: days === d ? 'linear-gradient(135deg, #7c3aed, #6d28d9)' : 'var(--input-bg)',
              color: days === d ? '#fff' : 'var(--text-secondary)',
            }}>{d}j</button>
          ))}
        </div>
        <button onClick={load} style={{
          padding: '6px 12px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer',
          border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text-secondary)',
          display: 'flex', alignItems: 'center', gap: '6px',
        }}><RefreshCw size={13} /> Rafraîchir</button>
      </div>

      {loading && <div style={{ ...card, textAlign: 'center', color: 'var(--text-secondary)' }}>Chargement…</div>}

      {!loading && data && anomalies.length === 0 && (
        <div style={{ ...card, color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.5 }}>
          Aucun signal sur la période. <strong>Cela ne garantit rien sur la qualité des réponses</strong> — seulement
          qu'aucun seuil d'exploitation n'est franchi (latence, déséquilibres de provenance).
        </div>
      )}

      {!loading && anomalies.map((a, i) => {
        const sev = SEVERITY[a.severity] || SEVERITY.info;
        return (
          <div key={`${a.type}-${i}`} style={{ ...card, borderLeft: `3px solid ${sev.color}`, padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <AlertTriangle size={15} style={{ color: sev.color, flexShrink: 0 }} />
              <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text)' }}>{a.label}</span>
              <span style={{
                padding: '2px 8px', borderRadius: '10px', fontSize: '10.5px', fontWeight: 600,
                background: `${sev.color}22`, color: sev.color,
              }}>{sev.label}</span>
              {a.metric != null && a.threshold != null && (
                <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'ui-monospace, monospace' }}>
                  mesuré {a.metric} · seuil {a.threshold}
                </span>
              )}
            </div>
            <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)', marginTop: '6px', lineHeight: 1.5 }}>{a.detail}</div>
            {a.trace_id && (
              <div style={{ marginTop: '8px' }}><CopyTrace traceId={a.trace_id} /></div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// restricted = vue curée (fondateur) : Performance + Flux + Provenance,
// SANS Services (infra brute) ni Décision (placeholder), ni coûts cross-projet.
const DataMemoryMap = ({ restricted = false }) => {
  const visibleTabs = restricted ? TABS.filter(t => ['perf', 'flux', 'provenance'].includes(t.key)) : TABS;
  const [tab, setTab] = useState(restricted ? 'provenance' : 'services');

  return (
    <div style={{ padding: '24px', height: '100%', overflow: 'auto' }}>
      <div style={{ marginBottom: '18px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Network size={22} style={{ color: '#8b5cf6' }} />
          Data &amp; Memory Map
        </h2>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
          Carte interne : performances, flux documentaires et traces de contexte IA — provenance prudente, souveraine et vérifiable.
        </p>
      </div>

      {/* Onglets */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '18px', flexWrap: 'wrap', borderBottom: '1px solid var(--card-border)', paddingBottom: '10px' }}>
        {visibleTabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
            border: '1px solid ' + (tab === t.key ? 'transparent' : 'var(--card-border)'),
            background: tab === t.key ? 'linear-gradient(135deg, #7c3aed, #6d28d9)' : 'transparent',
            color: tab === t.key ? '#fff' : 'var(--text-secondary)',
            display: 'flex', alignItems: 'center', gap: '7px',
          }}>{t.icon}{t.label}</button>
        ))}
      </div>

      {tab === 'services' && !restricted && <InfrastructureDashboard />}
      {tab === 'perf' && <PerfPanel restricted={restricted} />}
      {tab === 'flux' && <FluxPanel />}
      {tab === 'provenance' && <ProvenancePanel />}
      {tab === 'anomalies' && !restricted && <AnomaliesPanel />}
      {tab === 'decision' && !restricted && <DecisionPanel />}
    </div>
  );
};

export default DataMemoryMap;
